import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../../config/URL";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { MultiSelect } from "react-multi-select-component"; // make sure this is installed

function CurriculumCodeCopy({ id, onSuccess, handleMenuClose }) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [courseOptions, setCourseOptions] = useState([]);
  const [curriculumOptions, setCurriculumOptions] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedCurriculumOutlets, setSelectedCurriculumOutlets] = useState(
    []
  );

  const handleShow = async () => {
    setShow(true);
    try {
      const res = await api.get("/getAllCourses"); // Load course options when modal opens
      const options = res.data.map((course) => ({
        label: course.courseName,
        value: course.id,
      }));
      setCourseOptions(options);
    } catch (error) {
      toast.error("Failed to fetch courses");
    }
  };

  const handleClose = () => {
    handleMenuClose();
    setShow(false);
  };

  const validationSchema = Yup.object({
    courseIds: Yup.array().min(1, "*At least one course is required"),
    curoutIds: Yup.array().min(1, "*At least one curriculum outlet is required"),
  });

  const formik = useFormik({
    initialValues: {
      courseIds: [],
      curoutIds: [],
      curcodeIds: [id],
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await api.post("/reusableCurriculumCodes", values);
        if (response.status === 200 || response.status === 201) {
          toast.success(response.data.message);
          onSuccess();
          handleClose();
        } else {
          toast.error(response.data.message);
        }
      } catch (err) {
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchCurriculumOutlets = async () => {
      if (formik.values.courseIds.length > 0) {
        try {
          const res = await api.get(`/getCurriculumOutLetByCourseIds`, {
            params: {
              courseIds: formik.values.courseIds,
            },
            paramsSerializer: (params) => {
              return params.courseIds.map((id) => `courseIds=${id}`).join("&");
            },
          });

          const outletOptions = res.data.map((outlet) => ({
            label: outlet.outletName || outlet.name,
            value: outlet.id,
          }));

          setCurriculumOptions(outletOptions);
        } catch (error) {
          toast.error("Failed to fetch curriculum outlets");
        }
      } else {
        setCurriculumOptions([]);
        setSelectedCurriculumOutlets([]);
        formik.setFieldValue("curoutIds", []);
      }
    };

    fetchCurriculumOutlets();
  }, [formik.values.courseIds]);

  return (
    <>
      <p
        className="text-start mb-0 menuitem-style"
        onClick={handleShow}
        style={{ whiteSpace: "nowrap", width: "100%" }}
      >
        Curriculum Code Copy
      </p>

      <Dialog
        open={show}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            height: "400px",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>Curriculum Code Copy</DialogTitle>

        <form
          onSubmit={formik.handleSubmit}
          style={{ display: "flex", flexDirection: "column", flex: 1 }}
        >
          <DialogContent style={{ flex: 1, overflowY: "auto" }}>
            <div className="row">
              {/* Courses - 6 columns */}
              <div className="col-md-6 mb-4">
                <label className="form-label mb-2">
                  Courses<span className="text-danger">*</span>
                </label>
                <MultiSelect
                  options={courseOptions}
                  value={selectedCourses}
                  onChange={(selected) => {
                    setSelectedCourses(selected);
                    formik.setFieldValue(
                      "courseIds",
                      selected.map((option) => option.value)
                    );
                  }}
                  labelledBy="Select Courses"
                  className={`form-multi-select ${
                    formik.touched.courseIds && formik.errors.courseIds
                      ? "is-invalid"
                      : ""
                  }`}
                  onBlur={() => formik.setFieldTouched("courseIds", true)}
                />
                {formik.touched.courseIds && formik.errors.courseIds && (
                  <div className="text-danger mt-1 fs-6">
                    {formik.errors.courseIds}
                  </div>
                )}
              </div>

              {/* Curriculum Outlets - 6 columns */}
              <div className="col-md-6 mb-4">
                <label className="form-label mb-2">
                  Curriculum Outlets<span className="text-danger">*</span>
                </label>
                <MultiSelect
                  options={curriculumOptions}
                  value={selectedCurriculumOutlets}
                  onChange={(selected) => {
                    setSelectedCurriculumOutlets(selected);
                    formik.setFieldValue(
                      "curoutIds",
                      selected.map((option) => option.value)
                    );
                  }}
                  labelledBy="Select Curriculum Outlets"
                  className={`form-multi-select ${
                    formik.touched.curoutIds && formik.errors.curoutIds
                      ? "is-invalid"
                      : ""
                  }`}
                  onBlur={() => formik.setFieldTouched("curoutIds", true)}
                />
                {formik.touched.curoutIds && formik.errors.curoutIds && (
                  <div className="text-danger mt-1 fs-6">
                    {formik.errors.curoutIds}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>

          <DialogActions
            style={{
              borderTop: "1px solid #eee",
              padding: "16px",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-sm btn-success"
              disabled={loading}
            >
              {loading && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
              )}
              Submit
            </button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default CurriculumCodeCopy;
